import { promiseWithTimeout } from './lib/promise'
import { type Verdict } from './models/verdict'
import { GenericError } from './errors/GenericError'
import {
  type DelegationMessage,
  type DelegationResponse,
  type LoginMessage,
  type VerdictMessage,
} from './delegation'
import { getChromeRuntime } from './chrome/wrappers'
import { safeStringify } from './utils/jsonUtils'

/**
 * Provides one-way messaging to send delegation actions to another extension.
 *
 * @param extensionId - Identifier of the extension that will listen delegation actions.
 */
export class DelegationController {
  private readonly MESSAGE_TIMEOUT_MS = 15000

  extensionId: string

  constructor(idExtension: string) {
    this.extensionId = idExtension
  }

  /**
   * Returns if the extension is logged-in or not.
   *
   * @returns {Promise<boolean>} Promise that contains whether the user is logged-in or not. Can reject with {@link GenericError}.
   */
  isLoggedIn = async (): Promise<boolean> =>
    await this.sendMessage({ action: 'IS_LOGGED_IN' })

  /**
   * Logs-in the user.
   *
   * @param token - Token to authenticate the extension.
   *
   * @returns {Promise<void>} Empty Promise. Can reject with {@link GenericError} or {@link LoginError}.
   */
  login = async (token: string): Promise<void> => {
    const loginMessage: LoginMessage = { action: 'LOGIN', token }
    await this.sendMessage(loginMessage)
  }

  /**
   * Logs-out the user from the extension.
   *
   * @returns {Promise<void>} Empty Promise. Can reject with {@link GenericError}.
   */
  logout = async (): Promise<void> => {
    await this.sendMessage({ action: 'LOGOUT' })
  }

  /**
   * Returns if delegation is currently active or not.
   *
   * @returns {Promise<boolean>} Promise that contains whether delegation is currently active or not. Can reject with {@link GenericError}.
   */
  isDelegationActive = async (): Promise<boolean> =>
    await this.sendMessage({ action: 'IS_DELEGATION_ACTIVE' })

  /**
   * Start delegation control.
   *
   * @returns {Promise<void>} Empty Promise. Can reject with {@link GenericError} or {@link StartDelegationError}.
   */
  startDelegation = async (): Promise<void> => {
    await this.sendMessage({ action: 'START_DELEGATION' })
  }

  /**
   * Stop delegation control.
   *
   * @returns {Promise<void>} Empty Promise. Can reject with {@link GenericError} or {@link StopDelegationError}.
   */
  stopDelegation = async (): Promise<void> => {
    await this.sendMessage({ action: 'STOP_DELEGATION' })
  }

  /**
   * Sends the web request verdict.
   * Whether if a web connection has been allowed, blocked...
   *
   * @param requestId - The ID of the request given by the webRequest API
   * @param safeSearch - Whether safe search was applied or not for this request
   * @param verdict - The resolution of the connection (allowed, blocked...)
   * @returns {Promise<Verdict>} Promise that contains the verdict applied by the listener. Can reject with {@link GenericError}.
   */
  sendVerdict = async (
    requestId: string,
    safeSearch: boolean,
    verdict: Verdict
  ): Promise<Verdict> => {
    const sendVerdictMessage: VerdictMessage = {
      action: 'SEND_VERDICT',
      requestId,
      safeSearch,
      verdict,
    }
    return await this.sendMessage(sendVerdictMessage)
  }

  /**
   * Returns if should ignore Safety Net or not.
   *
   * @returns {Promise<boolean>} Promise that contains whether Safety Net should be ignored. Can reject with {@link GenericError}.
   */
  shouldIgnoreSafetyNet = async (): Promise<boolean> =>
    await this.sendMessage({ action: 'SHOULD_IGNORE_SAFETY_NET' })

  /**
   * Sends a delegation message to another extension.
   *
   * @param message - Delegation message with the action and extra data.
   * @returns Promise with the action response or error.
   */
  private readonly sendMessage = async <T>(
    message: DelegationMessage
  ): Promise<T> => {
    const sendMessagePromise = getChromeRuntime()
      .sendMessage(this.extensionId, message)
      .catch(async (_) => {
        // An error occurred when sending the message
        const runtimeError = getChromeRuntime()?.lastError?.message ?? ''
        console.error(
          `${message.action} action was sent but something went wrong. ${runtimeError}`
        )
        throw new GenericError('MESSAGE_NOT_RECEIVED', undefined, runtimeError)
      })
      .then(async (response) => {
        return await this.getMessageResponse<T>(
          response as DelegationResponse,
          message
        )
      })

    return await promiseWithTimeout(
      sendMessagePromise,
      this.MESSAGE_TIMEOUT_MS,
      new GenericError(
        'NOT_RESPONDING',
        undefined,
        `Response was not received after waiting ${this.MESSAGE_TIMEOUT_MS}ms.`
      )
    )
  }

  private readonly getMessageResponse = async <T>(
    response: DelegationResponse,
    message: DelegationMessage
  ): Promise<T> => {
    if (response.error !== undefined) {
      // Response contains an error
      console.error(
        `${message.action} action received an error: ${safeStringify(
          response.error
        )}.`
      )
      throw response.error
    }
    return response.response as T
  }
}
